<template>
  <div class="w-full flex flex-col gap-4">
    <!-- Main Content -->
    <div class="flex flex-col gap-4">
      <!-- Currency/Network Selector or Monitoring -->
      <div class="flex flex-col gap-3">
        <!-- Monitoring State -->
        <div v-if="isMonitoring" class="p-4 bg-white/5 border border/10 rounded-xl">
          <DepositStatusMonitor
            :address="monitoringAddress"
            :currency="monitoringCurrency"
            :currency-info="walletCurrency"
            :rpc-url="walletNetwork.rpcUrl"
            :chain="walletNetwork.id"
            :auto-start="true"
            @deposit-received="handleDepositReceived"
            @timeout="handleTimeout"
            @error="handleError"
            ref="monitorRef"
          />
          <button
            type="button"
            class="btn-ghost w-full !py-2 !text-sm mt-3"
            @click="resetSelection"
          >
            {{ $t('common.back') }}
          </button>
        </div>

        <!-- Selection State -->
        <div v-else>
          <h3 class="text-base font-semibold text-vault-text-primary mb-3">
            {{ $t('currencySelector.title') }}
          </h3>

          <!-- Currency Dropdown -->
          <div class="border-2 border/10 rounded-xl overflow-hidden">
            <CurrencyDropdown
              ref="currencyDropdownRef"
              :wallet-address="walletAddress"
              :wallet-currency="walletCurrency"
              :wallet-network="walletNetwork"
              :native-currency="{ currency: walletCurrency, network: walletNetwork }"
              @select="handleOptionSelect"
            />
          </div>

          <!-- Wallet Address / Payin Address (for direct and exchange deposits) -->
          <div v-if="selectedOption && depositAddress" class="mt-3 p-4 bg-white/5 border border/10 rounded-xl">
            <!-- Address Display with QR -->
            <div class="flex flex-col items-center gap-3">
              <div class="w-full flex justify-center">
                <div class="border-2 border/10 p-1 px-3 rounded-xl bg-white/5">
                  <AddressDisplay
                    :address="depositAddress"
                    :truncate="true"
                    :show-copy-hint="true"
                    :show-qr-button="true"
                  />
                </div>
              </div>

              <!-- Exchange Amount Info -->
              <div v-if="selectedOption.type === 'exchange' && exchangeTransaction" class="w-full mt-2 text-xs text-vault-text-secondary space-y-1">
                <div class="flex items-center justify-between py-1">
                  <span>{{ $t('cryptoDeposit.sendExactly') }}</span>
                  <span class="font-semibold text-vault-text-primary">
                    {{ formatAmount(exchangeTransaction.amountExpectedFrom) }} {{ exchangeTransaction.fromCurrency.toUpperCase() }}
                  </span>
                </div>
                <div class="flex items-center justify-between py-1">
                  <span>{{ $t('cryptoDeposit.youWillReceive') }}</span>
                  <span class="font-semibold text-vault-text-primary">
                    {{ formatAmount(exchangeTransaction.amountExpectedTo) }} {{ exchangeTransaction.toCurrency.toUpperCase() }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading/Creating Transaction State (for exchange only) -->
          <div
            v-if="selectedOption && selectedOption.type === 'exchange' && !exchangeTransaction"
            class="mt-3 p-4 bg-white/5 rounded-lg border border-white/10"
          >
            <div class="flex items-center justify-center py-2">
              <div class="spinner-small"></div>
              <span class="ml-2 text-xs text-vault-text-secondary">{{ $t('cryptoDeposit.creatingTransaction') }}</span>
            </div>
          </div>

          <!-- Transaction Error -->
          <div v-if="transactionError" class="mt-3 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-xs text-red-500">
            {{ transactionError }}
          </div>
        </div>
      </div>
    </div>

    <!-- Warning for Exchange -->
    <div
      v-if="selectedOption && selectedOption.type === 'exchange'"
      class="p-3 bg-orange-500/5 border border-orange-500/20 rounded-lg flex items-start gap-2 text-xs"
    >
      <AlertTriangle :size="16" class="text-orange-500/90 flex-shrink-0 mt-0.5" />
      <div class="flex flex-col gap-1">
        <p class="font-semibold text-orange-500/90">
          {{ $t('cryptoDeposit.exchangeWarning') }}
        </p>
        <p class="text-vault-text-secondary">
          {{ $t('cryptoDeposit.exchangeDescription') }}
        </p>
      </div>
    </div>

    <!-- Back Button (moved to bottom, full width) -->
    <button
      type="button"
      class="w-full btn-ghost !px-3 !py-2 !text-sm flex items-center justify-center gap-2"
      @click="$emit('back')"
    >
      <ArrowLeft :size="16" />
      {{ $t('common.back') }}
    </button>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ArrowLeft, AlertTriangle } from 'lucide-vue-next'
import AddressDisplay from './AddressDisplay.vue'
import CurrencyDropdown from './CurrencyDropdown.vue'
import DepositStatusMonitor from './DepositStatusMonitor.vue'
import { useCryptoDeposit } from '../../composables/useCryptoDeposit'

const { t: $t } = useI18n()

const props = defineProps({
  walletAddress: {
    type: String,
    required: true
  },
  walletCurrency: {
    type: Object, // CurrencyConfig
    required: true
  },
  walletNetwork: {
    type: Object, // { id, name, rpcUrl }
    required: true
  },
  targetAmountUsd: {
    type: Number,
    default: 100 // Default $100 for exchange deposits
  }
})

const emit = defineEmits(['back', 'deposit-received'])

// Composable
const cryptoDeposit = useCryptoDeposit({
  walletAddress: props.walletAddress,
  walletCurrency: props.walletCurrency,
  walletNetwork: props.walletNetwork
})

// State
const monitorRef = ref(null)
const isMonitoring = ref(false)
const selectedOption = ref(null)
const monitoringAddress = ref(props.walletAddress)
const monitoringCurrency = ref(props.walletCurrency.code)

// Exchange transaction state
const exchangeTransaction = ref(null)
const transactionError = ref(null)
const isCreatingTransaction = ref(false)

// Auto-refresh interval for fixed rate
let refreshIntervalId = null

// Computed: Deposit address (wallet address for direct, payin address for exchange)
const depositAddress = computed(() => {
  if (!selectedOption.value) return props.walletAddress

  if (selectedOption.value.type === 'direct') {
    return props.walletAddress
  }

  if (selectedOption.value.type === 'exchange' && exchangeTransaction.value) {
    return exchangeTransaction.value.payinAddress
  }

  return null
})

// Cleanup on unmount
onUnmounted(() => {
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId)
    refreshIntervalId = null
  }
})

// Format amount for display
function formatAmount(amount) {
  const num = parseFloat(amount)
  if (isNaN(num)) return '0'

  // For very small amounts, use exponential notation
  if (num < 0.0001 && num > 0) {
    return num.toExponential(4)
  }

  // For larger amounts, use fixed decimals
  if (num >= 1) {
    return num.toFixed(4)
  }

  return num.toFixed(8)
}

// Create exchange transaction automatically
async function createExchangeTransaction() {
  if (!selectedOption.value || selectedOption.value.type !== 'exchange') {
    return
  }

  console.log('[CryptoDepositWidget] Creating exchange transaction for $', props.targetAmountUsd)

  try {
    isCreatingTransaction.value = true
    transactionError.value = null

    // 1. Calculate fixed rate for target amount
    const estimate = await cryptoDeposit.calculateExchangeEstimate(props.targetAmountUsd)

    // 2. Create Changelly fixed rate transaction
    await cryptoDeposit.createExchangeTransaction(estimate.sourceAmount)

    // 3. Store transaction data
    const transaction = cryptoDeposit.transaction.value
    if (transaction && transaction.payinAddress) {
      exchangeTransaction.value = transaction
      console.log('[CryptoDepositWidget] Exchange transaction created:', transaction.changellyTxId)
    } else {
      throw new Error('Failed to get transaction data')
    }
  } catch (err) {
    console.error('[CryptoDepositWidget] Failed to create exchange transaction:', err)
    transactionError.value = err.message || 'Failed to create transaction'
  } finally {
    isCreatingTransaction.value = false
  }
}

// Refresh exchange transaction (renew fixed rate)
async function refreshExchangeTransaction() {
  if (!selectedOption.value || selectedOption.value.type !== 'exchange') {
    return
  }

  console.log('[CryptoDepositWidget] Refreshing exchange transaction')

  try {
    // Create new transaction with fresh fixed rate
    await createExchangeTransaction()
  } catch (err) {
    console.error('[CryptoDepositWidget] Failed to refresh transaction:', err)
    // Don't overwrite existing transaction if refresh fails
  }
}

// Watch for selectedOption change to auto-create exchange transaction
watch(() => selectedOption.value, async (newOption, oldOption) => {
  // Clear refresh interval when changing options
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId)
    refreshIntervalId = null
  }

  if (newOption && newOption.type === 'exchange') {
    // Reset state
    exchangeTransaction.value = null
    transactionError.value = null

    // Create transaction automatically
    await createExchangeTransaction()

    // Start auto-refresh every 10 seconds
    refreshIntervalId = setInterval(() => {
      refreshExchangeTransaction()
    }, 10000) // 10 seconds
  }
})

// Handle option selection
async function handleOptionSelect(option) {
  selectedOption.value = option
  await cryptoDeposit.selectOption(option)

  // For direct deposit, just show the address and QR
  if (option.type === 'direct') {
    monitoringAddress.value = props.walletAddress
    monitoringCurrency.value = props.walletCurrency.code
  }
  // For exchange, watch will automatically trigger transaction creation
}

// Reset selection
function resetSelection() {
  // Clear refresh interval
  if (refreshIntervalId) {
    clearInterval(refreshIntervalId)
    refreshIntervalId = null
  }

  selectedOption.value = null
  exchangeTransaction.value = null
  transactionError.value = null
  isMonitoring.value = false
  monitoringAddress.value = props.walletAddress
  monitoringCurrency.value = props.walletCurrency.code
  cryptoDeposit.backToSelection()
}

// Handle deposit received
function handleDepositReceived(balance) {
  emit('deposit-received', balance)
}

// Handle monitoring timeout
function handleTimeout() {
  console.log('Deposit monitoring timed out')
}

// Handle monitoring error
function handleError(error) {
  console.error('Deposit monitoring error:', error)
}
</script>

<style scoped>
/* Styles removed - QR functionality moved to AddressDisplay component */
</style>
